import { Button, Divider, Space, Tree } from 'antd';
import { useUnit } from 'effector-react';
import {
  DoubleLeftOutlined,
  DownOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import style from './ProjectsTree.module.css';
import {
  $altaRailwayStartDots,
  $ferryTerminalsStartDots,
  $investProStartDots,
  $kzAirportsStartDots,
  $minresStartPolygons,
  $showProjectsTree,
  resetProjectModalEv,
} from '../../models/investProModel/index.js';
import {
  $activeProjects,
  changeActiveProjectsEv,
  clearActiveProjectsEv,
  selectAllEv,
} from '../../models/activeFiltersModel/index.js';
import {
  layers_colors_dictionary,
  layers_src_icons_dictionary,
} from '../../dictionaries/layers_dictionary.js';

function ProjectsTree() {
  const dots = useUnit($investProStartDots);
  const altaRailway = useUnit($altaRailwayStartDots);
  const kzAirports = useUnit($kzAirportsStartDots);
  const ferryTerminals = useUnit($ferryTerminalsStartDots);
  const minres = useUnit($minresStartPolygons);
  const activeProjects = useUnit($activeProjects);
  const changeActiveProjects = useUnit(changeActiveProjectsEv);
  const showProjectsTree = useUnit($showProjectsTree);
  const clearActiveProjects = useUnit(clearActiveProjectsEv);
  const selectAll = useUnit(selectAllEv);
  const resetProjectModal = useUnit(resetProjectModalEv);

  const onCheck = (nodes, e) => {
    if (window.dotPopups && window.dotPopups.length > 0) {
      window.dotPopups = window.dotPopups.filter((item) => {
        if (!nodes.includes(item.options.id)) {
          item.remove();
          resetProjectModal();
        }
        return nodes.includes(item.options.id);
      });
    }
    changeActiveProjects(nodes);
  };

  const onClear = () => {
    clearActiveProjects();
    if (window.dotPopups && window.dotPopups.length > 0) {
      window.dotPopups.forEach((item) => item.remove());
      window.dotPopups = [];
    }
  };

  const formatData = () => {
    let data = {};
    dots.forEach((item) => {
      if (data[item.properties.gen_name]) {
        data[item.properties.gen_name].push(item);
      } else {
        data[item.properties.gen_name] = [item];
      }
    });

    data = Object.entries(data).map(([key, value]) => {
      return {
        title: (
          <div className={style.tree_item_title}>
            <img src={layers_src_icons_dictionary[key]} />
            <div
              className={style.circle}
              style={{ backgroundColor: layers_colors_dictionary[key] }}
            />
            {key}
          </div>
        ),
        key,
        children: value.map((item) => {
          return {
            title: item.properties.name_ru,
            key: item.properties.id,
          };
        }),
      };
    });

    data = [
      {
        title: 'Инвест проекты',
        key: 'invest_projects',
        children: data,
      },
      {
        title: 'Специальные зоны',
        key: 'special_zones',
        children: [
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.iz} />
                <div
                  className={style.circle}
                  style={{ backgroundColor: layers_colors_dictionary.iz }}
                />
                Индустриальные зоны
              </div>
            ),
            key: 'iz',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.sez} />
                <div
                  className={style.circle}
                  style={{ backgroundColor: layers_colors_dictionary.sez }}
                />
                Специальные экономические зоны
              </div>
            ),
            key: 'sez',
          },
        ],
      },
      {
        title: 'Логистика и транспорт',
        key: 'logistic_and_transport',
        children: [
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.alta_railway} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.alta_railway,
                  }}
                />
                Железнодорожная инфраструктура
              </div>
            ),
            key: 'altaRailway',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.kz_airports} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.kz_airports,
                  }}
                />
                Аэропорты
              </div>
            ),
            key: 'kzAirports',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.kz_helicopter} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.kz_helicopter,
                  }}
                />
                Вертолетные площадки
              </div>
            ),
            key: 'kzHelicopter',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.ferry_terminals} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.ferry_terminals,
                  }}
                />
                Морской и речной порты
              </div>
            ),
            key: 'ferryTerminals',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.azs} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.azs,
                  }}
                />
                АЗС
              </div>
            ),
            key: 'azs',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.warehouse} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.warehouse,
                  }}
                />
                Склады
              </div>
            ),
            key: 'warehouse',
          },
          {
            title: (
              <div className={style.tree_item_title}>
                <img src={layers_src_icons_dictionary.sto} />
                <div
                  className={style.circle}
                  style={{
                    backgroundColor: layers_colors_dictionary.sto,
                  }}
                />
                СТО
              </div>
            ),
            key: 'sto',
          },
        ],
      },
      {
        title: (
          <div className={style.tree_item_title}>
            <img src={layers_src_icons_dictionary.minres} />
            <div
              className={style.circle}
              style={{ backgroundColor: layers_colors_dictionary.minres }}
            />
            Природные ресурсы
          </div>
        ),
        key: 'minres',
      },
    ];

    return data;
  };

  return (
    <div
      className={[
        style.projects_tree,
        showProjectsTree ? style.show : style.hide,
        'glass',
      ].join(' ')}
    >
      <Space>
        <Button onClick={selectAll} className={style.projects_tree_button}>
          Выбрать все
        </Button>
        <Button onClick={onClear} className={style.projects_tree_button}>
          Очистить
        </Button>
      </Space>
      <div className={style.category_title}>Инвест проекты</div>
      <Tree
        checkedKeys={activeProjects}
        defaultExpandedKeys={['invest_projects']}
        treeData={formatData()}
        switcherIcon={<DownOutlined />}
        multiple
        checkable
        selectable={false}
        onCheck={onCheck}
      />
    </div>
  );
}

export default ProjectsTree;
